// src/pages/Calendar/CalendarSetupPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Banner from "../../components/layout/Banner";
import {
  getMyCalendarSetupPrefill,
  getMyFinancialCalendarProfile,
  getMyFinancialCalendarQuestionnaire,
  saveMyFinancialCalendarQuestionnaire,
  refreshMyFinancialActions,
} from "../../lib/actions/calendarSetup";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const INITIAL = {
  registration_month: "",
  financial_calendar: "",
  tax_type: "",
  vat_registered: null,
  vat_due_timing: "",
  paye_registered: null,
  uif_registered: null,
};
const YES_NO = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

function Question({ number, title, hint, children }) {
  return (
    <section className="space-y-3 rounded-[20px] border border-[#E5E5E5] bg-white p-5">
      <div className="flex gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#FFF0E6] text-xs font-black text-[#C85015]">
          {number}
        </span>
        <div>
          <h2 className="text-[15px] font-black leading-snug text-[#222222]">{title}</h2>
          {hint && <p className="mt-1 text-xs text-[#757575]">{hint}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function Dropdown({ value, onChange, options, label }) {
  return (
    <select
      aria-label={label}
      value={value == null ? "" : String(value)}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border-2 border-[#E0E0E0] bg-white px-4 py-3 text-[14px] font-semibold text-[#333333] outline-none transition-colors focus:border-[#FF914D] focus:ring-2 focus:ring-[#FF914D]/15"
    >
      <option value="">Select an answer</option>
      {options.map(({ value: optionValue, label: optionLabel }) => (
        <option key={optionValue} value={optionValue}>{optionLabel}</option>
      ))}
    </select>
  );
}

function BooleanDropdown({ value, onChange, label }) {
  return (
    <Dropdown
      label={label}
      value={typeof value === "boolean" ? String(value) : ""}
      options={YES_NO}
      onChange={(next) => onChange(next === "" ? null : next === "true")}
    />
  );
}

export default function CalendarSetupPage() {
  const navigate = useNavigate();
  const [values, setValues] = useState(INITIAL);
  const [isCompany, setIsCompany] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [questionnaire, profile, prefill] = await Promise.all([
          getMyFinancialCalendarQuestionnaire(),
          getMyFinancialCalendarProfile(),
          getMyCalendarSetupPrefill(),
        ]);
        if (!active) return;
        // Entity type comes from onboarding; answers come ONLY from the new questionnaire.
        const kind = String(prefill?.legal_nature || profile?.entity_type || "")
          .trim().toLowerCase();
        setIsCompany(
          ["company", "private_company", "private company", "pty ltd", "close corporation", "close_corporation", "cc"].includes(kind)
        );
        if (questionnaire) {
          setValues({
            ...INITIAL,
            ...questionnaire,
            registration_month: questionnaire.registration_month == null
              ? "" : String(questionnaire.registration_month),
          });
        }
      } catch (e) {
        if (active) setError(e?.message || "Could not load the questionnaire.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  function change(name, value) {
    setError("");
    setWarning("");
    setValues((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "vat_registered" && value !== true ? { vat_due_timing: "" } : {}),
    }));
  }

  const valid = useMemo(() => (
    (!isCompany || Boolean(values.registration_month)) &&
    Boolean(values.financial_calendar) &&
    Boolean(values.tax_type) && values.tax_type !== "unknown" &&
    typeof values.vat_registered === "boolean" &&
    (!values.vat_registered || (Boolean(values.vat_due_timing) && values.vat_due_timing !== "unknown")) &&
    typeof values.paye_registered === "boolean" &&
    typeof values.uif_registered === "boolean"
  ), [values, isCompany]);

  async function save() {
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    setWarning("");
    try {
      await saveMyFinancialCalendarQuestionnaire({
        ...values,
        registration_month: isCompany ? values.registration_month : null,
      });
      // A reminder-generation failure must never invalidate saved questionnaire answers.
      try {
        await refreshMyFinancialActions();
      } catch (refreshError) {
        console.warn("Calendar saved; action refresh failed:", refreshError);
      }
      navigate("/progress-dashboard", {
        replace: true,
        state: { calendarSetupCompleted: true },
      });
    } catch (e) {
      setError(e?.message || "Could not save calendar answers. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center font-sans text-[#757575]">Loading financial calendar...</div>;
  }

  return (
    <div className="min-h-screen bg-[#F4F4F6] pb-32 font-sans">
      <div className="bg-white"><Banner homeTo="/progress-dashboard" /></div>
      <main className="mx-auto w-full max-w-[760px] space-y-4 px-4 py-6">
        <div className="mb-6">
          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">Financial Calendar</p>
          <h1 className="mt-1 text-[25px] font-black text-[#111111]">Set up your calendar</h1>
          <p className="mt-2 text-[12px] leading-relaxed text-[#757575]">Answer these questions to personalise your financial and compliance reminders.</p>
        </div>
        {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
        {warning && <div role="status" className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">{warning}</div>}

        {isCompany && (
          <Question number="1" title="(If a company/close corp) In which month was your business first registered?">
            <Dropdown
              label="Business registration month"
              value={values.registration_month}
              onChange={(v) => change("registration_month", v)}
              options={MONTHS.map((name, i) => ({ value: String(i + 1), label: name }))}
            />
          </Question>
        )}

        <Question number="2" title="What financial calendar do you follow?">
          <Dropdown
            label="Financial calendar"
            value={values.financial_calendar}
            onChange={(v) => change("financial_calendar", v)}
            options={["Mar - Feb", "Jan - Dec", "Jun - Jul"].map((v) => ({ value: v, label: v }))}
          />
          {values.financial_calendar === "Jun - Jul" && (
            <p className="text-[11px] text-[#757575]">We'll save “Jun - Jul” exactly as selected. No precise statutory year-end will be assumed.</p>
          )}
        </Question>

        <Question number="3" title="What type of tax are you registered with SARS?">
          <Dropdown
            label="SARS tax type"
            value={values.tax_type}
            onChange={(v) => change("tax_type", v)}
            options={[
              { value: "provisional", label: "Provisional tax (standard for company, close corp, sole prop and partnership)" },
              { value: "turnover", label: "Turnover tax" },
              { value: "none", label: "I don't pay tax" },
            ]}
          />
        </Question>

        <Question number="4" title="Are you registered for VAT with SARS?">
          <BooleanDropdown label="VAT registration" value={values.vat_registered} onChange={(v) => change("vat_registered", v)} />
        </Question>

        {values.vat_registered === true && (
          <Question number="5" title="When is your next VAT payment due?">
            <Dropdown
              label="Next VAT payment month"
              value={values.vat_due_timing}
              onChange={(v) => change("vat_due_timing", v)}
              options={[
                { value: "this_month", label: "This month" },
                { value: "next_month", label: "Next month" },
                ]}
            />
            <p className="text-[11px] text-[#757575]">This sets a planning month, not a confirmed SARS filing deadline.</p>
          </Question>
        )}

        <Question number="6" title="Are you registered for PAYE with SARS?">
          <BooleanDropdown label="PAYE registration" value={values.paye_registered} onChange={(v) => change("paye_registered", v)} />
        </Question>

        <Question number="7" title="Are you registered for UIF with SARS?">
          <BooleanDropdown label="UIF registration" value={values.uif_registered} onChange={(v) => change("uif_registered", v)} />
        </Question>

        <p className="text-[11px] leading-relaxed text-[#757575]">Tax dates shown in Gwin are reminders, not proof of submission. Verify statutory deadlines with the relevant authority.</p>
      </main>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#E8E8E8] bg-white" style={{ paddingBottom: "max(14px, env(safe-area-inset-bottom, 0px))" }}>
        <div className="mx-auto flex max-w-[760px] gap-3 px-4 pt-3">
          <button type="button" onClick={() => navigate(-1)} disabled={saving} className="rounded-xl px-4 py-3 text-sm font-bold text-[#777777]">← Back</button>
          <button type="button" onClick={save} disabled={!valid || saving} className="ml-auto rounded-xl bg-[#FF914D] px-6 py-3 text-sm font-black text-white disabled:bg-[#E5E5E5] disabled:text-[#999999]">
            {saving ? "Saving..." : "SAVE CALENDAR"}
          </button>
        </div>
      </div>
    </div>
  );
}
